/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { concat } from 'ramda'
import dayjs from 'dayjs'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'

import {
  AddManagerIcon,
  CreatorIcon,
  ManagerIcon,
  UserIcon,
  RemoveUserIcon,
  RemoveManagerIcon,
  StudentIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import axios from 'src/utilities/http-client'
import { UserExtra as User } from 'src/user/user.types'
import useSession from 'src/authentication/hooks/use-session'
import {
  CertificateList,
  ExternalCertificateList,
} from 'src/v3/components/certificate-list'
import { CredentialList } from 'src/v3/components/credential-list'
import Loading from 'src/v3/components/loading'
import {
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectActiveLinkedService,
  selectUsersStatus,
  fetchUsers,
  selectUserById,
  fetchCertificates,
  selectCertificatesStatus,
  selectCertificatesByUserID,
  fetchLiveCertificates,
  selectLiveCertificatesByUserID,
  selectLiveCertificatesStatus,
  fetchExternalCertificates,
  selectExternalCertificatesByUserID,
  selectExternalCertificatesStatus,
  fetchCredentials,
  selectCredentialsStatus,
  selectCredentialsByUserId,
  selectClassesStatus,
  fetchClasses,
} from '../manage.slice'
import ConfirmAction from '../../components/confirm-action'
import Subheader from '../../components/subheader'

let removeUserFromRoster = (userID: string, linkedServiceID: string) =>
  axios.delete(
    `/api/v2/manage/roster/user?userID=${userID}&linkedServiceID=${linkedServiceID}`,
  )

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let session = useSession()
  let { userId } = useParams()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let usersStatus = useAppSelector(selectUsersStatus)
  let user = useAppSelector(selectUserById(userId!))
  let certificatesStatus = useAppSelector(selectCertificatesStatus)
  let certificates = useAppSelector(selectCertificatesByUserID(userId!))
  let liveCertificatesStatus = useAppSelector(selectLiveCertificatesStatus)
  let liveCertificates = useAppSelector(selectLiveCertificatesByUserID(userId!))
  let externalCertificatesStatus = useAppSelector(
    selectExternalCertificatesStatus,
  )
  let externalCertificates = useAppSelector(
    selectExternalCertificatesByUserID(userId!),
  )
  let credentialsStatus = useAppSelector(selectCredentialsStatus)
  let credentials = useAppSelector(selectCredentialsByUserId(userId!))
  let classesStatus = useAppSelector(selectClassesStatus)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let [profileImage, setProfileImage] = useState<string>('')
  let [open, setOpen] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')

  if (!user?.linkedServiceIDs?.includes(activeLinkedService)) {
    navigate('/manage/roster')
  }

  let toggleManager = (u: User) => {
    let newRoles = u.roles?.includes('manager')
      ? u.roles?.filter((name) => name !== 'manager')
      : concat(u.roles || [], ['manager'])

    return axios.put('/api/v2/manage/user/roles', {
      userID: u.id,
      roles: newRoles,
    })
  }

  let isManager = user?.roles?.includes('manager')

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers())
    }
  }, [usersStatus])

  useEffect(() => {
    if (certificatesStatus === 'uninitialized') {
      dispatch(fetchCertificates())
    }
  }, [certificatesStatus])

  useEffect(() => {
    if (liveCertificatesStatus === 'uninitialized') {
      dispatch(fetchLiveCertificates())
    }
  }, [liveCertificatesStatus])

  useEffect(() => {
    if (externalCertificatesStatus === 'uninitialized') {
      dispatch(fetchExternalCertificates())
    }
  }, [externalCertificatesStatus])

  useEffect(() => {
    if (credentialsStatus === 'uninitialized') {
      dispatch(fetchCredentials())
    }
  }, [credentialsStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (userId) {
      axios
        .get(`/api/v2/manage/profile-image?userID=${userId}`)
        .then(({ data }) => data)
        .then(setProfileImage)
        .catch(() => setProfileImage(''))
    }
  }, [userId])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  let verificationEnabled =
    linkedServices.find((ls) => ls.id === activeLinkedService)
      ?.credentialVerification === 'full' || false

  return (
    <>
      <div className="flex items-center justify-center my-2">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <UserIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          {user?.name}
        </Typography>
      </div>
      <Typography
        textAlign="center"
        color="text.secondary"
        sx={{ marginBottom: -1 }}
      >
        View & manage {user?.firstName}'s user information, certificates, and
        credentials
      </Typography>
      <Subheader title="User Profile" />
      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          alignItems: 'center',
          width: 'fit-content',
          p: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', marginX: 'auto' }}>
          {profileImage ? (
            <Avatar
              sx={{ width: 150, height: 150, marginX: 'auto' }}
              src={profileImage}
            />
          ) : (
            <Avatar
              sx={{
                width: 150,
                height: 150,
                marginX: 'auto',
                fontSize: 64,
              }}
            >
              {`${user?.firstName[0]}${user?.lastName[0]}`.toUpperCase()}
            </Avatar>
          )}
          <Typography variant="h6" sx={{ mt: 1 }}>
            {user?.name}
          </Typography>
          <div>{user?.phone}</div>
          <div>{user?.primaryEmail}</div>
          <div>{user?.address}</div>
          {user && user.city && user.state && user.zip && (
            <div>
              {user?.city}, {user?.state} {user?.zip}
            </div>
          )}
        </Box>
        <Box sx={{ marginX: 'auto' }}>
          <Typography variant="subtitle1" color="text.secondary">
            Roles
          </Typography>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              width: '100%',
              marginTop: 3,
              marginBottom: 12,
            }}
          >
            {[
              { name: 'Manager', icon: <ManagerIcon /> },
              { name: 'Creator', icon: <CreatorIcon /> },
              { name: 'Student', icon: <StudentIcon /> },
            ].map((role) => (
              <Chip
                key={role.name}
                label={role.name}
                color="primary"
                variant={
                  user?.roles?.includes(role.name.toLowerCase())
                    ? 'filled'
                    : 'outlined'
                }
                icon={role.icon}
              />
            ))}
          </div>
          <Divider sx={{ marginY: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">
            License
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}>
            <div>
              <strong>Number:</strong> {user?.licenseNumber || 'Unknown'}
            </div>
            <div>
              <strong>Type:</strong> {user?.licenseType || 'Unknown'}
            </div>
            <div>
              <strong>State:</strong> {user?.licenseState || 'Unknown'}
            </div>
            <div>
              <strong>Expiration:</strong>{' '}
              {user?.licenseExpiration
                ? dayjs(user?.licenseExpiration).format('MM/DD/YYYY')
                : 'Unknown'}
            </div>
          </div>
          <Divider sx={{ marginY: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">
            NREMT Certification
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 20 }}>
            <div>
              <strong>Number:</strong>{' '}
              {user?.nremtCertificationNumber || 'Unknown'}
            </div>
            <div>
              <strong>Date:</strong>{' '}
              {user?.nremtCertificationDate
                ? dayjs(user?.nremtCertificationDate).format('MM/DD/YYYY')
                : 'Unknown'}
            </div>
          </div>
          <Divider sx={{ marginY: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">
            Actions
          </Typography>
          <div
            style={{
              marginTop: 3,
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              disabled={session?.userId === user?.id}
              startIcon={isManager ? <RemoveManagerIcon /> : <AddManagerIcon />}
              onClick={() => {
                setOpen(user?.id || '')
              }}
            >
              {isManager ? 'Remove' : 'Add'} {user?.firstName} as Manager
            </Button>
            <Button
              variant="outlined"
              disabled={session?.userId === user?.id}
              startIcon={<RemoveUserIcon />}
              onClick={() => {
                setOpen(`remove-${user?.id}` || '')
              }}
            >
              Remove {user?.firstName} from Roster
            </Button>
          </div>
        </Box>
      </Paper>
      <ConfirmAction
        executeAction={() =>
          user
            ? toggleManager(user).then(() => dispatch(fetchUsers()))
            : Promise.resolve()
        }
        open={open}
        setOpen={setOpen}
        openText={user?.id || 'N/A'}
        title={'Confirm Role Change'}
        text={`Are you sure you want to ${isManager ? 'remove' : 'add'} ${
          user?.name
        } as a manager?`}
      />
      <ConfirmAction
        executeAction={() =>
          user
            ? removeUserFromRoster(user.id, activeLinkedService)
                .then(() => dispatch(fetchUsers()))
                .then(() => navigate('/manage/roster'))
            : Promise.resolve()
        }
        open={open}
        setOpen={setOpen}
        openText={`remove-${user?.id}`}
        title={'Confirm Roster Removal'}
        text={`Are you sure you want to remove ${user?.name} from this linked service's roster?`}
      />
      <Subheader title="Recorded Certificates" />
      <CertificateList
        certificates={certificates}
        certificateType="Recorded"
        setErrorMessage={setErrorMessage}
        domain="manage"
        user={user}
      />
      <Subheader title="Live Certificates" />
      <CertificateList
        certificates={liveCertificates}
        certificateType="Live"
        setErrorMessage={setErrorMessage}
        domain="manage"
        user={user}
      />
      <Subheader title="External Certificates" />
      <ExternalCertificateList
        certificates={externalCertificates}
        fetchCertificates={fetchExternalCertificates as any}
        setErrorMessage={setErrorMessage}
        config={{ userID: userId!, linkedServiceID: activeLinkedService }}
      />
      <Subheader title="Credentials" />
      <CredentialList
        credentials={credentials}
        verificationEnabled={verificationEnabled}
        fetchCredentials={fetchCredentials as any}
        setErrorMessage={setErrorMessage}
        userID={userId}
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[
          certificatesStatus,
          classesStatus,
          credentialsStatus,
          externalCertificatesStatus,
          linkedServicesStatus,
          liveCertificatesStatus,
          usersStatus,
        ]}
      />
    </>
  )
}
